import React, { useEffect, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  TextField, Typography, Box, Alert, Chip, Stack, Switch, FormControlLabel,
  InputAdornment,
} from "@mui/material";
import {
  PRC_PRIMARY_GRADIENT, PRC_PRIMARY_GRADIENT_HOVER, PRC_PRIMARY_SHADOW,
} from "./_pricingTheme";

export default function HandlingTierEditDialog({ item, onClose, onSubmit }) {
  const [draft, setDraft] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [localError, setLocalError] = useState(null);

  useEffect(() => {
    if (item) {
      setDraft({
        tier_name: item.tier_name ?? "",
        min_weight_kg: item.min_weight_kg ?? "",
        max_weight_kg: item.max_weight_kg ?? "",
        charge_inr: item.charge_inr ?? "",
        is_active: item.is_active ?? true,
        description: item.description ?? "",
      });
      setLocalError(null);
    }
  }, [item]);

  if (!item || !draft) return null;

  const isFullTruck = !!item.is_full_truck;

  const validate = () => {
    if (!String(draft.tier_name).trim()) return "Tier name cannot be empty";
    const c = parseFloat(draft.charge_inr);
    if (Number.isNaN(c) || c < 0) return "Charge (₹) must be a non-negative number";
    if (!isFullTruck) {
      const lo = draft.min_weight_kg === "" ? null : parseFloat(draft.min_weight_kg);
      const hi = draft.max_weight_kg === "" ? null : parseFloat(draft.max_weight_kg);
      if (lo != null && Number.isNaN(lo)) return "min_weight_kg must be numeric or blank";
      if (hi != null && Number.isNaN(hi)) return "max_weight_kg must be numeric or blank";
      if (lo != null && hi != null && lo >= hi) return "min_weight_kg must be less than max_weight_kg";
    }
    return null;
  };

  const handleSubmit = async () => {
    const err = validate();
    if (err) { setLocalError(err); return; }
    setLocalError(null);
    setSubmitting(true);

    const patch = {
      tier_name: String(draft.tier_name).trim(),
      charge_inr: parseFloat(draft.charge_inr),
      is_active: !!draft.is_active,
      description: draft.description == null ? null : String(draft.description),
    };
    if (!isFullTruck) {
      patch.min_weight_kg = draft.min_weight_kg === "" ? null : parseFloat(draft.min_weight_kg);
      patch.max_weight_kg = draft.max_weight_kg === "" ? null : parseFloat(draft.max_weight_kg);
    }

    const result = await onSubmit(item.id, patch);
    if (!result?.ok) {
      setLocalError(result?.message || "Save failed");
    }
    setSubmitting(false);
  };

  const update = (k) => (e) => setDraft({ ...draft, [k]: e.target.value });

  return (
    <Dialog
      open={!!item}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
          borderLeft: "5px solid",
          borderImage: "linear-gradient(180deg, #007C84, #016268) 1",
        },
      }}
    >
      <DialogTitle
        sx={{
          background: "linear-gradient(135deg, #F0F9FF, #EFF6FF)",
          borderBottom: "1px solid #E5E7EB",
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={800} sx={{ color: "#0F172A" }}>
            {item.tier_name}
          </Typography>
          <Typography variant="caption" sx={{ fontFamily: "monospace", color: "#007C84", fontWeight: 700 }}>
            {item.tier_code}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 3 }}>
        <Stack direction="row" spacing={1} sx={{ mb: 2.5 }} flexWrap="wrap" useFlexGap>
          <Chip
            size="small"
            label={isFullTruck ? "type · full_truck" : "type · partial"}
            sx={{
              fontWeight: 700,
              bgcolor: isFullTruck ? "#FEF3C7" : "#DBEAFE",
              color: isFullTruck ? "#92400E" : "#1E3A8A",
            }}
          />
          {!draft.is_active && (
            <Chip
              size="small"
              label="inactive"
              variant="outlined"
              sx={{ fontWeight: 700, color: "#DC2626", borderColor: "#FCA5A5" }}
            />
          )}
        </Stack>

        <TextField
          fullWidth size="small"
          label="Tier name (display)"
          value={draft.tier_name}
          onChange={update("tier_name")}
          sx={{ mb: 2 }}
        />

        {!isFullTruck && (
          <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
            <TextField
              fullWidth size="small" type="number"
              label="Min weight (kg)"
              value={draft.min_weight_kg}
              onChange={update("min_weight_kg")}
              inputProps={{ step: 0.5 }}
              helperText="Inclusive lower bound. Blank = open."
            />
            <TextField
              fullWidth size="small" type="number"
              label="Max weight (kg)"
              value={draft.max_weight_kg}
              onChange={update("max_weight_kg")}
              inputProps={{ step: 0.5 }}
              helperText="Exclusive upper bound. Blank = open."
            />
          </Stack>
        )}

        <TextField
          fullWidth size="small" type="number"
          label="Handling charge (₹)"
          value={draft.charge_inr}
          onChange={update("charge_inr")}
          inputProps={{ step: 0.5, min: 0 }}
          InputProps={{
            startAdornment: <InputAdornment position="start">₹</InputAdornment>,
          }}
          helperText="Flat fee added to the per-package preFee."
          sx={{ mb: 2 }}
        />

        <FormControlLabel
          control={
            <Switch
              checked={!!draft.is_active}
              onChange={(e) => setDraft({ ...draft, is_active: e.target.checked })}
              color="success"
            />
          }
          label={
            <Typography sx={{ fontWeight: 600, fontSize: 13 }}>
              Active (engine reads this row)
            </Typography>
          }
        />

        <TextField
          fullWidth size="small"
          label="Description"
          value={draft.description ?? ""}
          onChange={update("description")}
          multiline minRows={2}
          sx={{ mt: 2 }}
        />

        {localError && (
          <Alert severity="warning" sx={{ mt: 2 }}>{localError}</Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #E5E7EB" }}>
        <Button onClick={onClose} disabled={submitting} sx={{ textTransform: "none", fontWeight: 700 }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting}
          sx={{
            background: PRC_PRIMARY_GRADIENT,
            textTransform: "none",
            fontWeight: 700,
            px: 3,
            boxShadow: PRC_PRIMARY_SHADOW,
            "&:hover": { background: PRC_PRIMARY_GRADIENT_HOVER },
            "&.Mui-disabled": { background: "#E5E7EB", color: "#94A3B8" },
          }}
        >
          {submitting ? "Saving…" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
